import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DataGridLayout } from '../../../../wrapper-components/DataGrid/components/DataGridLayout/DataGridLayout';
import { DataGrid } from '../../../../wrapper-components/DataGrid/DataGrid';
import { useGetMandatoryAttributeNamesQuery } from '../../../create-project-wizard/api/createProjectApiSlice';
import { Concept, RequiredAttribute } from '../../types/response';

export const ReviewConceptAndAttributes = ({
  addedConcepts,
  isLoading,
}: {
  addedConcepts: Concept[];
  isLoading: boolean;
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const { data: mandatoryAttributeNamesData, isLoading: isMandatoryAttributesLoading } =
    useGetMandatoryAttributeNamesQuery();
  const mandatoryAttributeNames = mandatoryAttributeNamesData?.data ?? [];

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'conceptGroupName',
        headerName: 'Concepts',
        minWidth: 200,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        flex: 1,
      },
      {
        field: 'requiredAttributes',
        headerName: 'Attributes',
        minWidth: 300,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        flex: 2,
        renderCell: (params) => {
          const attributes: RequiredAttribute[] = params.row.requiredAttributes || [];
          const hasMandatory = mandatoryAttributeNames.length > 0;
          const hasRequired = attributes.length > 0;

          if (isMandatoryAttributesLoading) {
            return <Skeleton variant="rectangular" width="80%" height={24} />;
          }

          if (!hasMandatory && !hasRequired) {
            return (
              <Box sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                No attributes
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.spacing(1),
                py: theme.spacing(1),
              }}
            >
              {mandatoryAttributeNames.map((name) => (
                <Chip
                  key={`mandatory-${name}`}
                  label={name}
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    fontSize: '13px',
                    border: 0,
                  }}
                />
              ))}
              {attributes.map((attribute: RequiredAttribute) => (
                <Chip
                  key={attribute.id}
                  label={attribute.label}
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    fontSize: '13px',
                    border: 0,
                  }}
                />
              ))}
            </Box>
          );
        },
      },
    ],
    [theme, mandatoryAttributeNames, isMandatoryAttributesLoading]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        <Skeleton variant="rectangular" width="100%" height="150px" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">
          {formatMessage({ id: 'adminProjects.projectOverview.reviewConceptAndAttributes.title' })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{`${
            addedConcepts?.length ?? 0
          } `}</Typography>
          <Typography variant="body1">
            {formatMessage({
              id: 'adminProjects.projectOverview.reviewConceptAndAttributes.conceptCountSuffix',
            })}
          </Typography>
        </Box>
      </Box>

      <DataGridLayout id="review-concept-and-attributes-data-grid">
        <DataGrid
          id="review-concept-and-attributes-data-grid"
          mode="client"
          pagination
          initialPageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.conceptGroupId}
          columns={columns}
          rows={addedConcepts}
          useFetchDataGridData={() => {
            /* noop */
          }}
          sx={{
            maxHeight: '70vh',
            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
